import React, { ReactElement, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  blue50,
  gray10,
  gray20,
  gray40,
  gray80,
  gray100,
  green50,
  red50,
} from '../../../constants/colors';
import { boldMd, regularLg, regularSm, regularXl } from '../../../constants/location';
import { PatternHistory } from '../../../types/patternHistory';
import {
  buildItemListDiff,
  buildTextDiffSegments,
  hasAnyItemDiff,
  type ChildDiffEntry,
  type ItemDiffEntry,
  type TextSegment,
} from '../../../utils/pattern/historyDiff';

type DisplayMode = 'list' | 'json';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 比較元（古い側）。null の場合は v1 想定で全て追加扱い。 */
  baseHistory: PatternHistory | null;
  /** 比較先（新しい側）。 */
  targetHistory: PatternHistory;
  /**
   * JSON モードの差分表示を許可するか。
   * 一般ユーザーには表示せず、リスト表示のみ提供する（issue #2828）。
   * 省略時は false（JSON 非表示）。
   */
  showJsonMode?: boolean;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '32px',
      maxWidth: '880px',
      width: '100%',
      '@media screen and (max-width:960px)': {
        padding: '24px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  title: {
    ...regularXl,
    color: gray100,
    '@media screen and (max-width:960px)': { ...regularLg },
  },
  iconButton: { padding: 0 },
  icon: { height: '32px', width: '32px' },
  subTitle: {
    ...regularSm,
    color: gray80,
    marginBottom: '16px',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '16px',
  },
  sectionTitle: {
    ...boldMd,
    color: gray100,
    marginTop: '16px',
    marginBottom: '8px',
  },
  scrollArea: {
    maxHeight: '60vh',
    overflowY: 'auto',
    border: '1px solid ' + gray40,
    borderRadius: '4px',
    padding: '12px 16px',
    backgroundColor: gray10,
  },
  noChange: {
    ...regularSm,
    color: gray80,
    padding: '24px 0',
    textAlign: 'center' as const,
  },
  itemRow: {
    padding: '8px 12px',
    borderRadius: '4px',
    marginBottom: '6px',
    border: '1px solid ' + gray40,
    backgroundColor: '#fff',
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  itemFieldLabel: {
    ...regularSm,
    color: gray80,
    minWidth: '92px',
  },
  itemFieldValue: {
    ...regularSm,
    color: gray100,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
  badge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '4px',
    color: '#fff',
    fontWeight: 700,
  },
  badgeAdded: { backgroundColor: green50 },
  badgeRemoved: { backgroundColor: red50 },
  badgeChanged: { backgroundColor: gray80 },
  badgeMoved: { backgroundColor: blue50 },
  segmentEqual: { color: gray100 },
  segmentInsert: {
    backgroundColor: 'rgba(0, 163, 60, 0.15)',
    color: green50,
    textDecoration: 'none' as const,
  },
  segmentDelete: {
    backgroundColor: 'rgba(189, 0, 23, 0.15)',
    color: red50,
    textDecoration: 'line-through' as const,
  },
  childWrapper: {
    paddingLeft: '20px',
    borderLeft: `2px solid ${gray40}`,
    marginTop: '6px',
  },
  childRow: {
    padding: '4px 0',
  },
  jsonPane: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: '12px',
    lineHeight: '18px',
    backgroundColor: '#fff',
    border: '1px solid ' + gray40,
    borderRadius: '4px',
    padding: '12px',
    whiteSpace: 'pre' as const,
    overflowX: 'auto' as const,
    overflowY: 'auto' as const,
    maxHeight: '75vh',
  },
  jsonLineEqual: { display: 'block' },
  jsonLineInsert: {
    display: 'block',
    backgroundColor: 'rgba(0, 163, 60, 0.12)',
    color: green50,
  },
  jsonLineDelete: {
    display: 'block',
    backgroundColor: 'rgba(189, 0, 23, 0.12)',
    color: red50,
  },
  promptDiffArea: {
    backgroundColor: gray20,
    border: '1px solid ' + gray40,
    borderRadius: '4px',
    padding: '12px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: '12px',
    lineHeight: '20px',
    maxHeight: '60vh',
    overflowY: 'auto' as const,
  },
};

/**
 * 文字単位 diff のセグメント列を span でレンダリングする。
 */
const renderSegments = (segments: TextSegment[]): ReactElement => {
  return (
    <span>
      {segments.map((seg, idx) => {
        if (seg.type === 'equal') {
          return (
            <span key={idx} style={styles.segmentEqual}>
              {seg.text}
            </span>
          );
        }
        if (seg.type === 'insert') {
          return (
            <span key={idx} style={styles.segmentInsert}>
              {seg.text}
            </span>
          );
        }
        return (
          <span key={idx} style={styles.segmentDelete}>
            {seg.text}
          </span>
        );
      })}
    </span>
  );
};

/**
 * 1 行子項目の差分行
 */
const ChildRow = ({ child }: { child: ChildDiffEntry }): ReactElement | null => {
  if (child.status === 'unchanged') return null;
  if (child.status === 'added') {
    return (
      <Box sx={styles.childRow}>
        <Box sx={styles.itemHeader}>
          <Box sx={[styles.badge, styles.badgeAdded]}>追加</Box>
          <Typography sx={styles.itemFieldValue}>{child.after?.childValue ?? ''}</Typography>
        </Box>
        {child.after?.extractionRule && (
          <Typography sx={{ ...styles.itemFieldValue, color: gray80 }}>
            取得ルール: {child.after.extractionRule}
          </Typography>
        )}
      </Box>
    );
  }
  if (child.status === 'removed') {
    return (
      <Box sx={styles.childRow}>
        <Box sx={styles.itemHeader}>
          <Box sx={[styles.badge, styles.badgeRemoved]}>削除</Box>
          <Typography sx={styles.itemFieldValue}>{child.before?.childValue ?? ''}</Typography>
        </Box>
        {child.before?.extractionRule && (
          <Typography sx={{ ...styles.itemFieldValue, color: gray80 }}>
            取得ルール: {child.before.extractionRule}
          </Typography>
        )}
      </Box>
    );
  }
  const moveOnly =
    !!child.moved && !child.childValueDiff && !child.extractionRuleDiff && !child.outputFormatDiff;

  return (
    <Box sx={styles.childRow}>
      <Box sx={styles.itemHeader}>
        {!moveOnly && <Box sx={[styles.badge, styles.badgeChanged]}>変更</Box>}
        {child.moved && child.beforeIndex !== undefined && child.afterIndex !== undefined && (
          <Box sx={[styles.badge, styles.badgeMoved]}>
            並び替え {child.beforeIndex} → {child.afterIndex}
          </Box>
        )}
        <Box sx={styles.itemFieldValue}>
          {child.childValueDiff
            ? renderSegments(child.childValueDiff)
            : (child.after?.childValue ?? '')}
        </Box>
      </Box>
      {child.extractionRuleDiff && (
        <Box sx={{ ...styles.itemFieldValue, color: gray80 }}>
          取得ルール: {renderSegments(child.extractionRuleDiff)}
        </Box>
      )}
      {child.outputFormatDiff && (
        <Box sx={{ ...styles.itemFieldValue, color: gray80 }}>
          出力形式: {renderSegments(child.outputFormatDiff)}
        </Box>
      )}
    </Box>
  );
};

const ItemRow = ({ item }: { item: ItemDiffEntry }): ReactElement | null => {
  if (item.status === 'unchanged') return null;

  if (item.status === 'added') {
    return (
      <Box sx={styles.itemRow}>
        <Box sx={styles.itemHeader}>
          <Box sx={[styles.badge, styles.badgeAdded]}>追加</Box>
          <Typography sx={{ ...styles.itemFieldValue, fontWeight: 700 }}>
            {item.after?.parentValue ?? ''}
          </Typography>
        </Box>
        {item.after?.extractionRule && (
          <Typography sx={{ ...styles.itemFieldValue, color: gray80 }}>
            取得ルール: {item.after.extractionRule}
          </Typography>
        )}
        {item.childDiffs.length > 0 && (
          <Box sx={styles.childWrapper}>
            {item.childDiffs.map((c, i) => (
              <ChildRow key={i} child={c} />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  if (item.status === 'removed') {
    return (
      <Box sx={styles.itemRow}>
        <Box sx={styles.itemHeader}>
          <Box sx={[styles.badge, styles.badgeRemoved]}>削除</Box>
          <Typography sx={{ ...styles.itemFieldValue, fontWeight: 700 }}>
            {item.before?.parentValue ?? ''}
          </Typography>
        </Box>
        {item.before?.extractionRule && (
          <Typography sx={{ ...styles.itemFieldValue, color: gray80 }}>
            取得ルール: {item.before.extractionRule}
          </Typography>
        )}
        {item.childDiffs.length > 0 && (
          <Box sx={styles.childWrapper}>
            {item.childDiffs.map((c, i) => (
              <ChildRow key={i} child={c} />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // 並び替えのみの場合は「変更」バッジを出さず、「並び替え N → M」のみ表示する。
  // type/displayWidth/is_active のみ変わったケースは *Diff が無くても status='changed' に
  // なるが、moved ではないので「変更」バッジが出る挙動を維持する。
  const moveOnly =
    !!item.moved &&
    !item.parentValueDiff &&
    !item.extractionRuleDiff &&
    !item.outputFormatDiff &&
    item.childDiffs.length === 0;

  return (
    <Box sx={styles.itemRow}>
      <Box sx={styles.itemHeader}>
        {!moveOnly && <Box sx={[styles.badge, styles.badgeChanged]}>変更</Box>}
        {item.moved && item.beforeIndex !== undefined && item.afterIndex !== undefined && (
          <Box sx={[styles.badge, styles.badgeMoved]}>
            並び替え {item.beforeIndex} → {item.afterIndex}
          </Box>
        )}
        <Box sx={{ ...styles.itemFieldValue, fontWeight: 700 }}>
          {item.parentValueDiff
            ? renderSegments(item.parentValueDiff)
            : (item.after?.parentValue ?? '')}
        </Box>
      </Box>
      {item.extractionRuleDiff && (
        <Box sx={{ ...styles.itemFieldValue, color: gray80 }}>
          取得ルール: {renderSegments(item.extractionRuleDiff)}
        </Box>
      )}
      {item.outputFormatDiff && (
        <Box sx={{ ...styles.itemFieldValue, color: gray80 }}>
          出力形式: {renderSegments(item.outputFormatDiff)}
        </Box>
      )}
      {item.childDiffs.length > 0 && (
        <Box sx={styles.childWrapper}>
          {item.childDiffs.map((c, i) => (
            <ChildRow key={i} child={c} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * LCS（最長共通部分列）ベースの行単位 diff。
 * 配列中央の要素を1件だけ削除した場合に、前回実装のナイーブな
 * 「先頭の不一致行を探して同期する方式」だと後続の要素まで
 * 削除＋追加にマークしてしまうので、LCS の編集スクリプトで
 * 正確に削除/追加を区別する。
 */
const computeLineDiff = (
  beforeLines: string[],
  afterLines: string[],
): { type: 'equal' | 'insert' | 'delete'; text: string }[] => {
  const m = beforeLines.length;
  const n = afterLines.length;
  // dp[i][j] = LCS of beforeLines[0..i) and afterLines[0..j)
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (beforeLines[i - 1] === afterLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  const result: { type: 'equal' | 'insert' | 'delete'; text: string }[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (beforeLines[i - 1] === afterLines[j - 1]) {
      result.push({ type: 'equal', text: beforeLines[i - 1] });
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      result.push({ type: 'delete', text: beforeLines[i - 1] });
      i--;
    } else {
      result.push({ type: 'insert', text: afterLines[j - 1] });
      j--;
    }
  }
  while (i > 0) {
    result.push({ type: 'delete', text: beforeLines[i - 1] });
    i--;
  }
  while (j > 0) {
    result.push({ type: 'insert', text: afterLines[j - 1] });
    j--;
  }
  return result.reverse();
};

/**
 * JSON 行ベースの差分表示。
 * before/after の整形済み JSON を行単位で LCS diff にかけ、
 * INSERT/DELETE/EQUAL を行ごとに色分けする。
 */
const JsonDiffPane = ({ before, after }: { before: unknown; after: unknown }): ReactElement => {
  const lines = useMemo(() => {
    const beforeJson = JSON.stringify(before, null, 2) ?? '';
    const afterJson = JSON.stringify(after, null, 2) ?? '';
    if (beforeJson === afterJson) {
      return beforeJson.split('\n').map(l => ({ type: 'equal' as const, text: l }));
    }
    return computeLineDiff(beforeJson.split('\n'), afterJson.split('\n'));
  }, [before, after]);

  return (
    <Box sx={styles.jsonPane}>
      {lines.map((line, idx) => {
        const prefix = line.type === 'insert' ? '+ ' : line.type === 'delete' ? '- ' : '  ';
        const sx =
          line.type === 'insert'
            ? styles.jsonLineInsert
            : line.type === 'delete'
              ? styles.jsonLineDelete
              : styles.jsonLineEqual;
        return (
          <span key={idx} style={sx as React.CSSProperties}>
            {prefix}
            {line.text}
            {'\n'}
          </span>
        );
      })}
    </Box>
  );
};

const PatternHistoryDiffModal = ({
  open,
  onClose,
  baseHistory,
  targetHistory,
  showJsonMode = false,
}: Props): ReactElement => {
  const [mode, setMode] = useState<DisplayMode>('list');
  const effectiveMode: DisplayMode = showJsonMode ? mode : 'list';

  const beforeItems = baseHistory?.item_list ?? [];
  const afterItems = targetHistory.item_list ?? [];
  const beforePrompt = baseHistory?.prompt ?? '';
  const afterPrompt = targetHistory.prompt ?? '';
  const beforeUseCustomPrompt = baseHistory?.use_custom_prompt ?? false;
  const afterUseCustomPrompt = targetHistory.use_custom_prompt;

  const itemDiffs = useMemo(
    () => buildItemListDiff(beforeItems, afterItems),
    [beforeItems, afterItems],
  );
  const visibleItemDiffs = useMemo(
    () => itemDiffs.filter(d => d.status !== 'unchanged'),
    [itemDiffs],
  );
  const promptSegments = useMemo(
    () => buildTextDiffSegments(beforePrompt, afterPrompt),
    [beforePrompt, afterPrompt],
  );
  const promptChanged =
    beforePrompt !== afterPrompt || beforeUseCustomPrompt !== afterUseCustomPrompt;
  const showPrompt = afterUseCustomPrompt || beforeUseCustomPrompt;

  const noChanges = !hasAnyItemDiff(itemDiffs) && !promptChanged;

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog} maxWidth={false}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>変更差分</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.subTitle}>
        {baseHistory
          ? `v${baseHistory.version} → v${targetHistory.version} の差分`
          : `v${targetHistory.version} の内容`}
      </Typography>

      {showJsonMode && (
        <Box sx={styles.toolbar}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_, val): void => {
              if (val) setMode(val as DisplayMode);
            }}
          >
            <ToggleButton value="list">リスト</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {noChanges ? (
        <Typography sx={styles.noChange}>変更なし</Typography>
      ) : (
        <>
          {showPrompt && (
            <>
              <Typography sx={styles.sectionTitle}>カスタムプロンプト</Typography>
              {effectiveMode === 'list' ? (
                <Box sx={styles.promptDiffArea}>
                  {promptChanged ? renderSegments(promptSegments) : '変更なし'}
                </Box>
              ) : (
                <JsonDiffPane
                  before={{ use_custom_prompt: beforeUseCustomPrompt, prompt: beforePrompt }}
                  after={{ use_custom_prompt: afterUseCustomPrompt, prompt: afterPrompt }}
                />
              )}
            </>
          )}

          <Typography sx={styles.sectionTitle}>項目設定</Typography>
          {effectiveMode === 'list' ? (
            <Box sx={styles.scrollArea}>
              {visibleItemDiffs.length === 0 ? (
                <Typography sx={styles.noChange}>変更なし</Typography>
              ) : (
                visibleItemDiffs.map((item, i) => <ItemRow key={i} item={item} />)
              )}
            </Box>
          ) : (
            <JsonDiffPane before={beforeItems} after={afterItems} />
          )}
        </>
      )}
    </Dialog>
  );
};

export default PatternHistoryDiffModal;
